import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteConfig,
  apiDeleteReplacement,
  apiGetAllConfigs,
  apiGetAllReplacements,
  apiGetDevicesInformation
} from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import { communityList, convertLocaltimeToUTC, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { ADMIN_ROLE } from '@utils/constant';
import { useRole } from '@contexts/Auth/UseRole';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const { RangePicker } = DatePicker;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const [dateRange, setDateRange] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [period, setPeriod] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const { isWebAdmin, isWebManager, isWebSuperAdmin } = useRole();
  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllConfigs({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        serialNo: serialNo ?? undefined,
        period: selectedMonth ?? undefined
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/config/update/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/config/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá cấu hình ghi số này?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteConfig(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        render: (text, record) => {
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record?.status === '1' ? (
                  <CheckCircleFilled style={{ fontSize: '20px', color: '#52c41a' }} />
                ) : (
                  <CloseCircleFilled style={{ fontSize: '20px', color: '#ff4d4f' }} />
                )}
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    color: record?.status === '1' ? '#52c41a' : '#ff4d4f'
                  }}>
                  {record?.status === '1' ? 'Đã ghi' : 'Chưa ghi'}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 120
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Tên KH',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 150
      },
      {
        title: 'Tháng cước',
        dataIndex: 'period',
        render: (text, record) => {
          return <>{moment(record?.period)?.format('MM/YYYY')}</>;
        },
        width: 100
      },
      {
        title: 'Ngày bắt đầu ghi số',
        dataIndex: 'start_record_date',
        render: (text, record) => {
          return <>{moment(record?.start_record_date)?.format('DD/MM/YYYY')}</>;
        },
        width: 150
      },
      {
        title: 'Ngày đọc chỉ số',
        dataIndex: 'end_record_date',
        render: (text, record) => {
          return <>{moment(record?.end_record_date)?.format('DD/MM/YYYY')}</>;
        },
        width: 150
      },

      {
        title: 'Người tạo',
        dataIndex: 'created_by',
        width: 100
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {(isWebAdmin || isWebManager || isWebSuperAdmin) && (
              <>
                <Tooltip title="Chỉnh sửa">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>

                <Tooltip title="Xoá">
                  <Button
                    type="danger"
                    shape="circle"
                    icon={<DeleteFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const generateYearOptions = () => {
    const years = [];
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 1; y--) {
      years.push({ label: `Năm ${y}`, value: y });
    }
    return years;
  };
  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const isCurrentYear = selectedYear === current.year();
    const startOfYear = moment(`${selectedYear}-01-01`); // Start of selected year
    const endMonth = isCurrentYear ? current.month() + 1 : 11; // Limit to current month if in current year

    for (let m = 0; m <= endMonth; m++) {
      const monthMoment = startOfYear.clone().add(m, 'months');
      months.push({
        label: `Tháng cước ${monthMoment.format('MM/YYYY')}`,
        value: monthMoment.format('YYYY-MM-DD')
      });
    }
    return months;
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedMonth(null); // Reset month selection when year changes
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/configs/export-report`,
        `Danh sách cấu hình chốt số kỳ ${moment(selectedMonth).format('MM-YYYY')}.xlsx`,
        {
          keyWord: search ?? undefined,
          period: selectedMonth ?? undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    if (user?.user?.role !== ADMIN_ROLE) {
      showWarning({ message: 'Xin lỗi, bạn không có quyền thực hiện chức năng này' });
      return;
    }
    navigate('/config/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Quản lý lịch ghi chỉ số
          </Typography.Title>
        </Col>
        <Col>
          {(isWebAdmin || isWebManager || isWebSuperAdmin) && (
            <Space>
              {/* <Button
              icon={<PlusOutlined />}
              onClick={handleAddDevice}
              disabled
              style={{
                color: '#0174CF',
                height: '36px',
                backgroundColor: '#E8EBEF',
                borderColor: '#E8EBEF',
                fontSize: '18px'
              }}>
              Thêm mới
            </Button> */}
              <Button
                icon={<UploadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  setVisibleModalImport(true);
                }}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Nhập file excel
              </Button>
            </Space>
          )}
        </Col>
      </Row>
      <Card>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <Select
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              style={{ width: '100%', height: '36px' }}>
              {communityList.map((community) => (
                <Option key={community.value} value={community.value}>
                  {community.label}
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col md={3} xs={6}>
            <Select
              style={{ width: '100%', height: '36px' }}
              placeholder="Chọn năm"
              value={selectedYear}
              onChange={handleYearChange}>
              {generateYearOptions().map((year) => (
                <Option key={year.value} value={year.value}>
                  {year.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={5} xs={6}>
            <Select
              style={{ width: '100%', height: '36px' }}
              placeholder="Chọn tháng cước"
              value={selectedMonth}
              onChange={handleMonthChange}>
              {generateMonthOptions().map((month) => (
                <Option key={month.value} value={month.value}>
                  {month.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={5} xs={6}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col>

          <Col md={5} sm={6}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          // size="middle"
          loading={isFetching}
          dataSource={deviceList || []}
          columns={columns}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            onChange: handleChangePage,
            onShowSizeChange: handleChangePageSize,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          style={{
            borderRadius: '4px',
            thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
            fontSize: '30px'
          }}
          scroll={{ x: 'max-content', y: 600 }}
        />
      </Card>
    </div>
  );
}
